import { useState, useRef, useEffect } from "react";
import { Loader2, ArrowDown } from "lucide-react";

const THRESHOLD = 70; // px pull needed to trigger refresh
const MAX_PULL = 110; // hard cap on visual pull distance
const RESISTANCE = 0.5; // dampening factor for pull distance

/**
 * Mobile-only pull-to-refresh. Uses touch events so it never activates
 * on desktop (no touch = no listeners fire). Prevents the browser's
 * native pull-to-reload via overscroll-behavior on <html>.
 */
export default function PullToRefresh({ onRefresh, children }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const activeRef = useRef(false);
  const containerRef = useRef(null);

  // Manual touchmove listener (non-passive) so we can preventDefault
  // and block iOS rubber-band / Chrome pull-to-reload from stealing the gesture.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleTouchMove = (e) => {
      if (!activeRef.current || refreshing || startY.current == null) return;
      if (window.scrollY > 0) {
        activeRef.current = false;
        setPullDistance(0);
        return;
      }
      const diff = e.touches[0].clientY - startY.current;
      if (diff > 0) {
        const distance = Math.min(MAX_PULL, diff * RESISTANCE);
        setPullDistance(distance);
        if (e.cancelable) e.preventDefault();
      }
    };

    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", handleTouchMove);
  }, [refreshing]);

  const onTouchStart = (e) => {
    if (refreshing) return;
    if (window.scrollY > 0) return;
    startY.current = e.touches[0].clientY;
    activeRef.current = true;
  };

  const onTouchEnd = async () => {
    if (!activeRef.current || refreshing) {
      activeRef.current = false;
      startY.current = null;
      return;
    }
    const distance = pullDistance;
    activeRef.current = false;
    startY.current = null;

    if (distance >= THRESHOLD) {
      setRefreshing(true);
      setPullDistance(THRESHOLD);
      try {
        await onRefresh?.();
      } finally {
        setRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  };

  const state = refreshing
    ? "refreshing"
    : pullDistance >= THRESHOLD
    ? "release"
    : "pull";

  const opacity = refreshing ? 1 : Math.min(1, pullDistance / THRESHOLD);
  const transition =
    refreshing || pullDistance === 0
      ? "height 220ms ease-out, opacity 220ms ease-out"
      : "none";

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      data-testid="pull-to-refresh"
    >
      <div
        data-testid="pull-to-refresh-indicator"
        data-state={state}
        aria-hidden={pullDistance === 0 && !refreshing}
        className="flex items-center justify-center overflow-hidden md:hidden"
        style={{ height: `${pullDistance}px`, opacity, transition }}
      >
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#60a5fa]">
          {state === "refreshing" ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Refreshing…
            </>
          ) : state === "release" ? (
            <>
              <ArrowDown className="h-3.5 w-3.5 rotate-180 transition-transform" />
              Release to refresh
            </>
          ) : (
            <>
              <ArrowDown className="h-3.5 w-3.5 transition-transform" />
              Pull to refresh
            </>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
